import { McpServer, ResourceTemplate } from '@modelcontextprotocol/sdk/server/mcp.js';
import fs from 'fs';
import path from 'path';
import { fileURLToPath } from 'url';
import logger from '../utils/logger.js';
import { readTemplate } from '../utils/file-utils.js';

// Get the directory name from the current file's URL
const __dirname = path.dirname(fileURLToPath(import.meta.url));

// Path to templates directory
const TEMPLATES_DIR = path.resolve(__dirname, '../templates');

/**
 * Register all resources with the MCP server
 * @param server MCP server instance
 */
export async function registerResources(server: McpServer): Promise<void> {
  logger.info('Registering Artillery template resources');
  
  // Register a resource to list available templates
  server.resource(
    'templates',
    'artillery://templates',
    async (uri) => {
      try {
        const templates = fs.readdirSync(TEMPLATES_DIR)
          .filter(file => file.endsWith('.yml'))
          .map(file => ({
            name: file,
            path: `artillery://templates/${file}`
          }));
        
        return {
          contents: [{
            uri: uri.href,
            text: JSON.stringify(templates, null, 2)
          }]
        };
      } catch (error) {
        logger.error(`Error listing templates: ${error}`);
        throw new Error('Failed to list templates');
      }
    }
  );
  
  // Register a resource to get a specific template
  server.resource(
    'template',
    new ResourceTemplate('artillery://templates/{filename}', { list: undefined }),
    async (uri, { filename }) => {
      try {
        if (typeof filename !== 'string') {
          throw new Error(`Invalid filename: ${filename}`);
        }
        const templatePath = path.join(TEMPLATES_DIR, filename);
        
        if (!fs.existsSync(templatePath)) {
          throw new Error(`Template not found: ${filename}`);
        }
        
        const content = fs.readFileSync(templatePath, 'utf-8');
        
        return {
          contents: [{
            uri: uri.href,
            text: content
          }]
        };
      } catch (error) {
        logger.error(`Error fetching template ${filename}: ${error}`);
        throw new Error(`Failed to fetch template: ${filename}`);
      }
    }
  );
  
  // Register a resource for HTTP GET test template
  server.resource(
    'http-get-template',
    'artillery://examples/http-get',
    async (uri) => {
      try {
        const template = readTemplate('http-get.yml');
        
        return {
          contents: [{
            uri: uri.href,
            text: template
          }]
        };
      } catch (error) {
        logger.error(`Error fetching HTTP GET template: ${error}`);
        throw new Error('Failed to fetch HTTP GET template');
      }
    }
  );
  
  // Register a resource for HTTP POST test template
  server.resource(
    'http-post-template',
    'artillery://examples/http-post',
    async (uri) => {
      try {
        const template = readTemplate('http-post.yml');
        
        return {
          contents: [{
            uri: uri.href,
            text: template
          }]
        };
      } catch (error) {
        logger.error(`Error fetching HTTP POST template: ${error}`);
        throw new Error('Failed to fetch HTTP POST template');
      }
    }
  );
  
  // Register a resource for complex flow test template
  server.resource(
    'complex-flow-template',
    'artillery://examples/complex-flow',
    async (uri) => {
      try {
        const template = readTemplate('complex-flow.yml');
        
        return {
          contents: [{
            uri: uri.href,
            text: template
          }]
        };
      } catch (error) {
        logger.error(`Error fetching complex flow template: ${error}`);
        throw new Error('Failed to fetch complex flow template');
      }
    }
  );
  
  // Register a resource for Artillery documentation
  server.resource(
    'artillery-docs',
    'artillery://docs',
    async (uri) => {
      return {
        contents: [{
          uri: uri.href,
          text: `# Artillery Documentation Resources
          
## Official Documentation
- Main documentation: https://www.artillery.io/docs
- HTTP engine docs: https://www.artillery.io/docs/reference/engines/http
- Test script reference: https://www.artillery.io/docs/reference/test-script

## Key Concepts
- Artillery test scripts are written in YAML
- They define the target system, load phase configuration, and scenarios
- Phases control how virtual users are created over time
- Scenarios define what each virtual user does
- Reports can be generated in HTML, JSON, or text formats

## Basic Structure
\`\`\`yaml
config:
  target: "https://example.com"
  phases:
    - duration: 60
      arrivalRate: 5
      name: "Warm up phase"

scenarios:
  - name: "Simple flow"
    flow:
      - get:
          url: "/api/status"
\`\`\`
`
        }]
      };
    }
  );
  
  logger.info('Artillery resources registered successfully');
}
